import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { SimulateurDemandeur, SimulateurExpert, TableauComparatifPacks, GrilleTarifaire } from '../components/BatimentSimulateurs';
import { Euro, Calculator, Users, BadgeCheck, Download, FileText, Package, Star, CheckCircle, ArrowRight, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function TarifsBatimentPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackButton to="/batiment-expertise" label="Retour à Bâtiment & Expertise" />
      </div>

      {/* Hero Section */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 mb-4">
              <Euro className="h-5 w-5 text-slate-600" />
              <span className="text-slate-600 text-sm font-semibold">TARIFS & SIMULATEURS</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Tarifs Bâtiment & Expertise Civile
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez nos tarifs transparents et utilisez nos simulateurs pour calculer 
              vos coûts ou vos revenus en tant qu'expert.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: 'Accès plateforme', value: 'GRATUIT', bgClass: 'bg-green-50 border-green-200', textClass: 'text-green-600' },
              { label: 'Économie max', value: '-33%', bgClass: 'bg-blue-50 border-blue-200', textClass: 'text-blue-600' },
              { label: 'Part expert', value: '80%', bgClass: 'bg-purple-50 border-purple-200', textClass: 'text-purple-600' },
              { label: 'À partir de', value: '90€', bgClass: 'bg-amber-50 border-amber-200', textClass: 'text-amber-600' },
            ].map((stat, index) => (
              <Card key={index} className={stat.bgClass}>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className={`text-2xl font-bold ${stat.textClass}`}>{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabs Navigation */}
          <Tabs defaultValue="grille" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto bg-white shadow-md p-1 gap-1">
              <TabsTrigger value="grille" className="py-3 data-[state=active]:bg-slate-600 data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-2" />
                Grille tarifaire
              </TabsTrigger>
              <TabsTrigger value="packs" className="py-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Package className="w-4 h-4 mr-2" />
                Packs crédits
              </TabsTrigger>
              <TabsTrigger value="simulateur-demandeur" className="py-3 data-[state=active]:bg-green-600 data-[state=active]:text-white">
                <Calculator className="w-4 h-4 mr-2" />
                Simulateur client
              </TabsTrigger>
              <TabsTrigger value="simulateur-expert" className="py-3 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <BadgeCheck className="w-4 h-4 mr-2" />
                Simulateur expert
              </TabsTrigger>
            </TabsList>

            {/* Onglet Grille Tarifaire */}
            <TabsContent value="grille" className="space-y-6">
              <GrilleTarifaire />
              
              {/* Abonnements */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-6 h-6 text-amber-500" />
                    Abonnements Entreprises
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Pro BTP */}
                    <div className="p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-blue-800">Abonnement Pro BTP</h3>
                          <p className="text-sm text-blue-600">Pour les professionnels</p>
                        </div>
                        <div className="text-right">
                          <span className="text-3xl font-bold text-blue-600">39€</span>
                          <span className="text-blue-600">/mois</span>
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {[
                          '+30 crédits offerts / mois',
                          'Tarif réduit : -20% sur les rapports',
                          'Accès prioritaire 24-48h',
                          'Suivi centralisé de chantiers'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Entreprise+ */}
                    <div className="p-6 bg-purple-50 rounded-xl border-2 border-purple-300 relative">
                      <Badge className="absolute -top-3 right-4 bg-purple-600 text-white font-semibold">RECOMMANDÉ</Badge>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-purple-800">Abonnement Entreprise+</h3>
                          <p className="text-sm text-purple-600">Pour les grandes structures</p>
                        </div>
                        <div className="text-right">
                          <span className="text-3xl font-bold text-purple-600">79€</span>
                          <span className="text-purple-600">/mois</span>
                        </div>
                      </div>
                      <ul className="space-y-2">
                        {[
                          '+80 crédits offerts / mois',
                          'Assistance juridique civile (O\'Scop)',
                          'Expert référent dédié',
                          'Tableau de bord multi-chantiers'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Onglet Packs Crédits */}
            <TabsContent value="packs" className="space-y-6">
              <TableauComparatifPacks />
              
              {/* Utilisation des crédits */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-6 h-6 text-blue-600" />
                    Utilisation des crédits
                  </CardTitle>
                  <CardDescription>
                    Convertissez vos crédits en prestations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { service: 'Expertise simple', credits: 50, icon: '📋' },
                      { service: 'Expertise terrain', credits: 120, icon: '🏗️' },
                      { service: 'Rapport complet', credits: 180, icon: '📊' },
                      { service: 'Constat', credits: 70, icon: '📸' },
                      { service: 'Litige civil', credits: '40/30min', icon: '⚖️' },
                    ].map((item, index) => (
                      <Card key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                        <CardContent className="p-4 text-center">
                          <span className="text-3xl mb-2 block">{item.icon}</span>
                          <p className="text-sm font-medium text-gray-700 mb-1">{item.service}</p>
                          <Badge className="bg-blue-600 text-white font-semibold">{item.credits} crédits</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Note explicative */}
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4 flex items-start gap-3">
                  <Info className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800">Comment fonctionnent les crédits ?</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Les crédits sont une monnaie interne permettant d'acheter nos services à tarif réduit. 
                      Plus vous achetez de crédits, plus le prix par crédit diminue, jusqu'à 33% d'économie avec le Pack Entreprise.
                      Les crédits n'expirent pas et peuvent être utilisés sur toutes nos prestations.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Onglet Simulateur Demandeur */}
            <TabsContent value="simulateur-demandeur">
              <SimulateurDemandeur />
            </TabsContent>

            {/* Onglet Simulateur Expert */}
            <TabsContent value="simulateur-expert" className="space-y-6">
              <SimulateurExpert />
              
              {/* Tarifs experts */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
                  <CardTitle>Adhésions & Crédits-Dossiers pour les Experts</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Adhésions */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        Adhésion annuelle
                      </h4>
                      <div className="space-y-3">
                        {[
                          { cat: 'Expert individuel', prix: 180, inclut: 'Référencement + outils' },
                          { cat: 'Bureau d\'études', prix: 450, inclut: '3 experts + modules' },
                          { cat: 'Expert Premium', prix: 750, inclut: 'Mise en avant + API' },
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{item.cat}</p>
                              <p className="text-xs text-gray-500">{item.inclut}</p>
                            </div>
                            <span className="font-bold text-purple-600">{item.prix}€/an</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Crédits-Dossiers */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-amber-600" />
                        Crédits-Dossiers (obligatoires)
                      </h4>
                      <div className="space-y-3">
                        {[
                          { pack: 'Pack 10', prix: 40, dossiers: 10, cout: 4 },
                          { pack: 'Pack 50', prix: 150, dossiers: 50, cout: 3, popular: true },
                          { pack: 'Pack 200', prix: 400, dossiers: 200, cout: 2 },
                        ].map((item, idx) => (
                          <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${item.popular ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{item.pack}</span>
                              {item.popular && <Badge className="bg-amber-600 text-white text-xs font-semibold">BEST</Badge>}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-amber-600">{item.prix}€</p>
                              <p className="text-xs text-gray-500">{item.cout}€/dossier</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* CTA Final */}
          <div className="mt-12">
            <Card className="bg-gradient-to-r from-slate-700 to-gray-800 text-white border-0">
              <CardContent className="py-8 text-center">
                <h2 className="text-2xl font-bold mb-4">Prêt à démarrer ?</h2>
                <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                  Créez votre compte gratuitement et accédez à tous nos services d'expertise bâtiment.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="bg-white text-slate-800 hover:bg-gray-100">
                    Créer un compte
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger la grille PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
